import React, { useEffect } from 'react';
import {
  defaultCountries,
  parseCountry,
  usePhoneInput,
  CountrySelector,
} from 'react-international-phone';
import 'react-international-phone/style.css';
import { Input } from '../../atoms/Input';
import { InternationalContactProps } from './types';
import {
  EndAdornmentContainer,
  OtpButton,
  VerifiedIconWrapper,
} from './styles';

// Verified icon component (simple SVG to avoid MUI dependency)
const VerifiedIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
    <path d="M9 12L11 14L15 10" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const VARIANT_LABELS = {
  cell: 'Mobile Number',
  work: 'Work Phone',
  home: 'Home Phone',
};

export const InternationalContact = React.forwardRef<HTMLInputElement, InternationalContactProps>(
  (
    {
      variant = 'cell',
      value,
      onChange,
      availableCountries = ['US', 'IN', 'GB', 'CA', 'AU', 'DE', 'FR', 'IT', 'ES', 'NL', 'SE', 'NO', 'DK', 'FI', 'BE', 'AT', 'CH', 'IE', 'NZ', 'SG', 'HK', 'JP', 'CN', 'KR', 'MY', 'TH', 'ID', 'PH', 'VN', 'AE', 'SA', 'IL', 'TR', 'EG', 'ZA', 'BR', 'MX', 'AR', 'CL', 'CO', 'PE', 'PL'],
      defaultCountry = 'IN',
      otpConfig,
      label,
      onValidate,
      ...restProps
    },
    ref
  ) => {
    const shouldUseDefaults = !availableCountries || availableCountries.length === 0;
    
    const allowedCountries = shouldUseDefaults
      ? []
      : availableCountries.map((country) => country.toLowerCase());

    const filteredCountries = shouldUseDefaults
      ? defaultCountries
      : defaultCountries.filter((c) => {
          try {
            const country = parseCountry(c);
            return allowedCountries.includes(country.iso2);
          } catch (error) {
            return false;
          }
        });

    // Determine default country
    const defaultIso2 = defaultCountry.toLowerCase();
    const hasDefaultCountry = filteredCountries.some((c) => {
      try {
        return parseCountry(c).iso2 === defaultIso2;
      } catch {
        return false;
      }
    });
    const resolvedDefaultCountry = hasDefaultCountry ? defaultIso2 : 'in';

    const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
      defaultCountry: resolvedDefaultCountry,
      value,
      disableFormatting: true,
      forceDialCode: true,
      countries: filteredCountries,
      onChange: (data) => {
        // forceDialCode always renders "+<dialCode>" even when the user hasn't
        // typed anything, so treat that bare-dial-code state as still-empty
        // rather than reporting it upstream as a real (invalid) phone number.
        const isDialCodeOnly = data.phone.replace(/\s+/g, '') === `+${data.country.dialCode}`;
        const normalized = isDialCodeOnly ? '' : data.phone;
        if (normalized !== value) {
          onChange(normalized);
        }
      },
    });

    // Validate phone number (basic validation - can be enhanced)
    useEffect(() => {
      if (onValidate && value) {
        // Basic validation: check if it has country code and sufficient digits
        const isValid = /^\+\d{10,15}$/.test(value);
        onValidate(isValid);
      }
    }, [value, onValidate]);

    // Keep the dial-code flag in sync when the caller's defaultCountry changes
    // after mount (e.g. an address autocomplete/import sets the Country field
    // to India after this field first rendered with the US default). Only
    // auto-switch while the field is still empty, so it never overrides a
    // number someone has actually started entering.
    useEffect(() => {
      if (!value && hasDefaultCountry && country.iso2 !== defaultIso2) {
        setCountry(defaultIso2);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultIso2, hasDefaultCountry]);

    // Use variant-specific label if no custom label provided
    const effectiveLabel = label !== undefined ? label : VARIANT_LABELS[variant];

    // Build start adornment with flag and country selector
    const startAdornment = (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginLeft: '0.5rem', 
        marginRight: '0.5rem',
        position: 'relative'
      }}>
        <CountrySelector
          selectedCountry={country.iso2}
          onSelect={({ iso2 }) => setCountry(iso2)}
          countries={filteredCountries}
          buttonStyle={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
          dropdownStyleProps={{
            style: {
              zIndex: 9999,
              maxHeight: '300px',
              overflowY: 'auto',
            }
          }}
        />
      </div>
    );

    // Build end adornment with OTP button and verified badge
    const endAdornment = otpConfig && (otpConfig.enabled || otpConfig.isVerified) ? (
      <EndAdornmentContainer>
        {otpConfig.enabled && (
          <OtpButton
            disabled={otpConfig.disabled}
            onClick={!otpConfig.disabled ? otpConfig.onSend : undefined}
          >
            {otpConfig.disabled ? 'Sending...' : 'Send Otp'}
          </OtpButton>
        )}
        {otpConfig.isVerified && (
          <VerifiedIconWrapper>
            <VerifiedIcon />
          </VerifiedIconWrapper>
        )}
      </EndAdornmentContainer>
    ) : undefined;

    return (
      <Input
        ref={(node) => {
          // Handle both refs
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
          if (inputRef && typeof inputRef === 'object') {
            (inputRef as any).current = node;
          }
        }}
        label={effectiveLabel}
        placeholder={effectiveLabel}
        value={inputValue}
        onChange={(e) => {
          handlePhoneValueChange(e);
        }}
        onPaste={(e) => {
          e.preventDefault();
        }}
        type="tel"
        autoComplete="off"
        startAdornment={startAdornment}
        endAdornment={endAdornment}
        labelStyle="outlined"
        {...restProps}
      />
    );
  }
);

InternationalContact.displayName = 'InternationalContact';

export type { InternationalContactProps, InternationalContactVariant, OtpConfig } from './types';
